import { useQuery } from "@tanstack/react-query";

import { statisticsService } from "@/services/statisticsService";
import type { AnalyticsBucket } from "@/types/statistics";

export const STATISTICS_QUERY_KEYS = {
  AVAILABLE_PERIOD: (botId?: number, accountUuid?: string) => [
    "statistics",
    "period",
    botId,
    accountUuid,
  ],
  SUMMARY: (
    startDate?: string,
    endDate?: string,
    accountUuid?: string,
    botId?: number,
    groupByCycleStartDay?: boolean
  ) => ["statistics", "summary", startDate, endDate, accountUuid, botId, groupByCycleStartDay],
  DAILY: (
    startDate?: string,
    endDate?: string,
    accountUuid?: string,
    botId?: number,
    groupByCycleStartDay?: boolean
  ) => ["statistics", "daily", startDate, endDate, accountUuid, botId, groupByCycleStartDay],
  TIME_SERIES: (
    startDate?: string,
    endDate?: string,
    bucket?: AnalyticsBucket,
    accountUuid?: string,
    botId?: number,
    groupByCycleStartDay?: boolean
  ) => [
    "statistics",
    "time-series",
    startDate,
    endDate,
    bucket,
    accountUuid,
    botId,
    groupByCycleStartDay,
  ],
  DEALS: (
    startDate?: string,
    endDate?: string,
    instrumentType?: string,
    stockAccountId?: string,
    botId?: number,
    groupByCycleStartDay?: boolean
  ) => [
    "statistics",
    "deals",
    startDate,
    endDate,
    instrumentType,
    stockAccountId,
    botId,
    groupByCycleStartDay,
  ],
};

export function useAvailablePeriod(botId?: number, accountUuid?: string) {
  return useQuery({
    queryKey: STATISTICS_QUERY_KEYS.AVAILABLE_PERIOD(botId, accountUuid),
    queryFn: () =>
      statisticsService.getAvailablePeriod({
        bot_id: botId,
        stock_account_id: accountUuid,
      }),
    retry: false,
  });
}

export function useSummaryStats(
  startDate?: string,
  endDate?: string,
  accountUuid?: string,
  botId?: number,
  groupByCycleStartDay?: boolean
) {
  return useQuery({
    queryKey: STATISTICS_QUERY_KEYS.SUMMARY(
      startDate,
      endDate,
      accountUuid,
      botId,
      groupByCycleStartDay
    ),
    queryFn: () =>
      statisticsService.getSummary({
        start_date: startDate,
        end_date: endDate,
        stock_account_id: accountUuid,
        bot_id: botId,
        group_by_cycle_start_day: groupByCycleStartDay,
      }),
    enabled: !!startDate && !!endDate,
    retry: false,
  });
}

export function useDailyStats(
  startDate?: string,
  endDate?: string,
  accountUuid?: string,
  botId?: number,
  groupByCycleStartDay?: boolean
) {
  return useQuery({
    queryKey: STATISTICS_QUERY_KEYS.DAILY(
      startDate,
      endDate,
      accountUuid,
      botId,
      groupByCycleStartDay
    ),
    queryFn: () =>
      statisticsService.getDailyStats({
        start_date: startDate,
        end_date: endDate,
        stock_account_id: accountUuid,
        bot_id: botId,
        group_by_cycle_start_day: groupByCycleStartDay,
      }),
    retry: false,
  });
}

export function useTimeSeries(
  startDate?: string,
  endDate?: string,
  bucket: AnalyticsBucket = "day",
  accountUuid?: string,
  botId?: number,
  groupByCycleStartDay?: boolean
) {
  return useQuery({
    queryKey: STATISTICS_QUERY_KEYS.TIME_SERIES(
      startDate,
      endDate,
      bucket,
      accountUuid,
      botId,
      groupByCycleStartDay
    ),
    queryFn: () =>
      statisticsService.getTimeSeries({
        start_date: startDate!,
        end_date: endDate!,
        bucket,
        stock_account_id: accountUuid,
        bot_id: botId,
        group_by_cycle_start_day: groupByCycleStartDay,
      }),
    enabled: !!startDate && !!endDate,
    retry: false,
  });
}

export function useDeals(
  startDate?: string,
  endDate?: string,
  instrumentType?: string,
  stockAccountId?: string,
  botId?: number,
  demo?: boolean,
  groupByCycleStartDay?: boolean
) {
  return useQuery({
    queryKey: STATISTICS_QUERY_KEYS.DEALS(
      startDate,
      endDate,
      instrumentType,
      stockAccountId,
      botId,
      groupByCycleStartDay
    ),
    queryFn: () =>
      statisticsService.getDeals({
        start_date: startDate,
        end_date: endDate,
        instrument_type: instrumentType,
        stock_account_id: stockAccountId,
        bot_id: botId,
        demo,
        group_by_cycle_start_day: groupByCycleStartDay,
      }),
    retry: false,
  });
}

export function useAggregatedDeals(
  startDate?: string,
  endDate?: string,
  instrumentType?: string,
  stockAccountId?: string,
  botId?: number,
  demo?: boolean,
  groupByCycleStartDay?: boolean
) {
  return useQuery({
    queryKey: [
      ...STATISTICS_QUERY_KEYS.DEALS(
        startDate,
        endDate,
        instrumentType,
        stockAccountId,
        botId,
        groupByCycleStartDay
      ),
      "aggregated",
    ],
    queryFn: () =>
      statisticsService.getAggregatedDeals({
        start_date: startDate,
        end_date: endDate,
        instrument_type: instrumentType,
        stock_account_id: stockAccountId,
        bot_id: botId,
        demo,
        group_by_cycle_start_day: groupByCycleStartDay,
      }),
    retry: false,
  });
}
